/** The steps of a task: tick, edit in place, remove, add. Saved as a whole list. */
import { useState } from "react";
import { Plus, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { TickButton } from "@/features/command/ui";
import { uid } from "./lib";
import type { ChecklistItem } from "./types";

export function ChecklistEditor({ value, onChange }: { value: ChecklistItem[]; onChange: (items: ChecklistItem[]) => void }) {
  const [text, setText] = useState("");
  const [editing, setEditing] = useState<string | null>(null);
  const [draft, setDraft] = useState("");

  const add = () => {
    const t = text.trim();
    if (!t) return;
    onChange([...value, { id: uid(), text: t.slice(0, 500), done: false }]);
    setText("");
  };
  const finishEdit = (id: string) => {
    const t = draft.trim();
    setEditing(null);
    if (!t) return onChange(value.filter((x) => x.id !== id));
    if (t !== value.find((x) => x.id === id)?.text) onChange(value.map((x) => (x.id === id ? { ...x, text: t.slice(0, 500) } : x)));
  };
  const done = value.filter((x) => x.done).length;

  return (
    <div className="space-y-1">
      {value.length > 0 && (
        <div className="mb-2 flex items-center gap-2">
          <div className="h-1 flex-1 overflow-hidden rounded-full bg-zinc-800">
            <div className="h-full rounded-full bg-emerald-400 transition-all" style={{ width: `${(done / value.length) * 100}%` }} />
          </div>
          <span className="text-[11px] tabular-nums text-zinc-500">{done} of {value.length}</span>
        </div>
      )}
      {value.map((item) => (
        <div key={item.id} className="group flex items-center gap-2.5 rounded-lg px-1.5 py-1 hover:bg-zinc-800/30">
          <TickButton done={!!item.done} onToggle={() => onChange(value.map((x) => (x.id === item.id ? { ...x, done: !x.done } : x)))} />
          {editing === item.id ? (
            <input
              autoFocus
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onBlur={() => finishEdit(item.id)}
              onKeyDown={(e) => { if (e.key === "Enter") (e.target as HTMLInputElement).blur(); if (e.key === "Escape") setEditing(null); }}
              className="min-w-0 flex-1 rounded bg-zinc-900 px-1.5 py-0.5 text-sm text-zinc-100 focus:outline-none focus:ring-1 focus:ring-indigo-500/40"
            />
          ) : (
            <button
              type="button"
              onClick={() => { setEditing(item.id); setDraft(item.text); }}
              className={cn("min-w-0 flex-1 truncate text-left text-sm", item.done ? "text-zinc-500 line-through" : "text-zinc-200")}
            >
              {item.text}
            </button>
          )}
          <button
            type="button"
            onClick={() => onChange(value.filter((x) => x.id !== item.id))}
            className="rounded p-0.5 text-zinc-600 opacity-0 transition-opacity hover:text-rose-300 group-hover:opacity-100 focus:opacity-100"
            aria-label="Remove step"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      ))}
      <form onSubmit={(e) => { e.preventDefault(); add(); }} className="flex items-center gap-2.5 px-1.5 pt-1">
        <Plus className="h-4 w-4 shrink-0 text-zinc-500" />
        <input
          value={text}
          onChange={(e) => setText(e.target.value)}
          onBlur={add}
          placeholder={value.length ? "Add another step" : "Add the first step"}
          className="min-w-0 flex-1 bg-transparent py-1 text-sm text-zinc-100 placeholder:text-zinc-500 focus:outline-none"
        />
      </form>
    </div>
  );
}
